import React, { useState } from 'react';
import { Card, Button, Badge } from 'react-bootstrap';
import { BsHouseDoor, BsDropletFill, BsRulers, BsHeart, BsHeartFill, BsHandThumbsUp, BsHandThumbsUpFill, BsHandThumbsDown, BsHandThumbsDownFill, BsTrophy, BsStar, BsStarFill } from 'react-icons/bs';

interface PropertyCardProps {
  property: {
    id: string;
    mls_number?: string;
    price: number;
    address: string;
    city: string;
    state: string;
    bedrooms: number;
    bathrooms: number;
    square_feet: number;
    images?: string[];
    matchScore?: number;
    relevanceScore?: number;
    ranking?: number;
    isTopMatch?: boolean;
    listing_brokerage?: string;
    listing_agent?: string;
  };
  onView?: () => void;
  onSave?: () => void;
  onFeedback?: (propertyId: string, feedback: 'positive' | 'negative') => void;
  isSaved?: boolean;
  userFeedback?: 'positive' | 'negative' | null;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ 
  property, 
  onView, 
  onSave,
  onFeedback,
  isSaved = false,
  userFeedback = null 
}) => {
  const [feedbackState, setFeedbackState] = useState<'positive' | 'negative' | null>(userFeedback);
  
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleFeedback = (type: 'positive' | 'negative') => {
    const newFeedback = feedbackState === type ? null : type;
    setFeedbackState(newFeedback);
    if (onFeedback) {
      onFeedback(property.id, newFeedback || type);
    }
  };

  const getRelevanceColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'secondary';
  };

  const getRelevanceStars = (score: number) => {
    const stars = Math.round(score / 20); // Convert 0-100 to 0-5 stars
    return stars;
  };

  return (
    <Card className="property-card h-100 shadow-sm">
      <div className="position-relative">
        {property.images && property.images[0] ? (
          <Card.Img 
            variant="top" 
            src={property.images[0]} 
            alt={property.address}
            style={{ height: '200px', objectFit: 'cover' }}
          />
        ) : (
          <div 
            className="bg-secondary d-flex align-items-center justify-content-center"
            style={{ height: '200px' }}
          >
            <span className="text-white">No Image Available</span>
          </div>
        )}
        
        {/* Best Match Badge */}
        {property.isTopMatch && (
          <Badge 
            bg="danger" 
            className="position-absolute top-0 start-0 m-2 d-flex align-items-center gap-1"
          >
            <BsTrophy /> Best Match
          </Badge>
        )}
        
        {/* Relevance Score */}
        {property.relevanceScore !== undefined && !property.isTopMatch && (
          <Badge 
            bg={getRelevanceColor(property.relevanceScore)} 
            className="position-absolute top-0 start-0 m-2"
          >
            {property.relevanceScore}% Match
          </Badge>
        )}
        
        {/* Ranking Badge */}
        {property.ranking && property.ranking <= 3 && !property.isTopMatch && (
          <Badge 
            bg="info" 
            className="position-absolute top-0 start-0 m-2"
            style={{ marginTop: property.relevanceScore !== undefined ? '35px' : '8px' }}
          >
            #{property.ranking}
          </Badge>
        )}
        
        {onSave && (
          <Button
            variant="light"
            size="sm"
            className="position-absolute top-0 end-0 m-2 rounded-circle"
            style={{ width: '40px', height: '40px', padding: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              onSave();
            }}
          >
            {isSaved ? <BsHeartFill color="red" /> : <BsHeart />}
          </Button>
        )}
      </div>
      
      <Card.Body>
        <h4 className="text-primary mb-1">{formatPrice(property.price)}</h4>
        <p className="text-muted mb-3">
          {property.address}<br />
          {property.city}, {property.state}
        </p>
        
        <div className="d-flex justify-content-between text-muted small mb-3">
          <span className="d-flex align-items-center gap-1">
            <BsHouseDoor /> {property.bedrooms} beds
          </span>
          <span className="d-flex align-items-center gap-1">
            <BsDropletFill /> {property.bathrooms} baths
          </span>
          <span className="d-flex align-items-center gap-1">
            <BsRulers /> {property.square_feet ? property.square_feet.toLocaleString() : 'N/A'} sqft
          </span>
        </div>

        {/* Relevance Stars */}
        {property.relevanceScore !== undefined && (
          <div className="d-flex justify-content-center mb-3">
            {[...Array(5)].map((_, i) => (
              <span key={i} className="mx-1">
                {i < getRelevanceStars(property.relevanceScore || 0) ? (
                  <BsStarFill className="text-warning" />
                ) : (
                  <BsStar className="text-muted" />
                )}
              </span>
            ))}
          </div>
        )}
        
        {/* Feedback Buttons */}
        {onFeedback && (
          <div className="d-flex justify-content-center gap-3 mb-3">
            <Button
              variant={feedbackState === 'positive' ? 'success' : 'outline-success'}
              size="sm"
              onClick={() => handleFeedback('positive')}
              className="d-flex align-items-center gap-1"
            >
              {feedbackState === 'positive' ? <BsHandThumbsUpFill /> : <BsHandThumbsUp />}
              <span className="small">Helpful</span>
            </Button>
            <Button
              variant={feedbackState === 'negative' ? 'danger' : 'outline-danger'}
              size="sm"
              onClick={() => handleFeedback('negative')}
              className="d-flex align-items-center gap-1"
            >
              {feedbackState === 'negative' ? <BsHandThumbsDownFill /> : <BsHandThumbsDown />}
              <span className="small">Not Helpful</span>
            </Button>
          </div>
        )}
        
        {onView && (
          <Button 
            variant="primary" 
            className="w-100"
            onClick={onView}
          >
            View Details
          </Button>
        )}
      </Card.Body>
    </Card>
  );
};

export default PropertyCard;